/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { upsertTournamentUser } from 'modules/players/store/actions';
import { useTeamDetails } from 'modules/teams/ui/hooks/use-team-details';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { AppDispatch } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Player, Team } from 'modules/players/domain/interfaces/player';
import playersRepository from 'modules/players/domain/repositories/player.repository';
import { resetMomentsData, selectCurrentPlayerId, selectProcess, setCurrentPlayerId } from 'modules/players/store';
import PlayerForm, { SubmitPlayerForm } from '../components/player-form';
import PlayerLoad from '../components/player-load';
import PlayerMomentsPage from '../components/player-moments-page';
import PlayerEditFormTabs from '../components/tabs-player-view';
import TeamsList from '../components/teams-list';

function PlayerViewPage() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();

  const id = Number(useParams().id);
  const navigate = useNavigate();
  if (isNaN(id)) {
    navigate(Pages.PLAYERS.INDEX);
  }

  const location = useLocation();
  const activeTab = (location.pathname || '').split('/').at(-1);

  const { info } = useOrganizer();
  const [isLoading, setIsLoading] = useState(true);
  const [player, setPlayer] = useState<Player>();
  const { isProcessed } = useSelector(selectProcess);
  const currentPlayerId = useSelector(selectCurrentPlayerId);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const { setHeaderContent } = useMainLayout();

  const teamId = Number(useLocation().state?.fromTeam);
  const { team: fromTeam, isLoading: teamFetching } = useTeamDetails(teamId);

  /**
   * Карточка всегда читает игрока сама. Раньше первым делом смотрели в
   * накопленный список из слайса, но списка больше нет: `/players` работает через
   * RTK Query, а сюда всё равно нужен полный игрок с составами и заявками.
   * `isProcessed` в зависимостях перечитывает карточку после сохранения.
   */
  useEffect(() => {
    playersRepository
      .getUser(id)
      .then(setPlayer)
      // Отказ (игрока нет, нет прав) — экран «Такой игрок не найден» и причина в тосте,
      // а не вечный скелетон с unhandled rejection.
      .catch((error) => toastApiError(error, msg`Не удалось загрузить игрока`))
      .finally(() => setIsLoading(false));
  }, [id, isProcessed, toastApiError]);

  useEffect(() => {
    if (id && id !== currentPlayerId) {
      dispatch(resetMomentsData());
      dispatch(setCurrentPlayerId({ playerId: id }));
    }
  }, [currentPlayerId, dispatch, id]);

  const fullName = useMemo(() => {
    return player && `${player?.surname} ${player?.name} ${player?.middleName ?? ''}`;
  }, [player]);

  const breadcrumbs = useMemo(() => {
    if (fromTeam) {
      const items = [
        { label: t`Команды`, to: Pages.TEAMS.INDEX },
        { label: fromTeam.name, to: Pages.TEAMS.builders.edit(fromTeam.id) },
        { label: t`Игроки`, to: Pages.TEAMS.builders.edit(fromTeam.id, 'players') },
        {
          label: fullName ?? t`Неизвестный игрок`,
          to: Pages.PLAYERS.builders.view(id),
          state: { fromTeam: fromTeam.id },
        },
      ];

      if (activeTab === 'teams') {
        items.push({
          label: t`Команды`,
          to: Pages.PLAYERS.builders.view(id, 'teams'),
          state: { fromTeam: fromTeam.id },
        });
      }

      if (activeTab === 'moments') {
        items.push({
          label: t`Моменты`,
          to: Pages.PLAYERS.builders.view(id, 'moments'),
          state: { fromTeam: fromTeam.id },
        });
      }

      return items;
    }

    const result = [
      { label: t`Игроки`, to: Pages.PLAYERS.INDEX },
      { label: fullName ?? t`Неизвестный игрок`, to: Pages.PLAYERS.builders.view(id) },
    ];

    if (activeTab === 'teams') {
      result.push({ label: t`Команды`, to: Pages.PLAYERS.builders.view(id, 'teams') });
    }

    if (activeTab === 'moments') {
      result.push({ label: t`Моменты`, to: Pages.PLAYERS.builders.view(id, 'moments') });
    }

    return result;
  }, [activeTab, fullName, id, fromTeam, t]);

  const breadcrumb = useMemo(() => <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbs} />, [breadcrumbs]);

  const teams = useMemo(() => {
    return (player?.positionsInTeams?.map((positionInTeam) => positionInTeam.team).filter(Boolean) as Team[]) ?? [];
  }, [player?.positionsInTeams]);

  const handleSubmit = useCallback<SubmitPlayerForm>(
    ({ values, formikHelpers }) => {
      if (!info) {
        return;
      }

      const photo = values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined;
      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      setIsLoadingPlayers(true);

      dispatch(
        upsertTournamentUser({
          userId: values?.id,
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          height,
          weight,
          photo,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(t`Игрок успешно сохранён`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось сохранить игрока`);
        })
        .finally(() => {
          formikHelpers.setSubmitting(false);
          setIsLoadingPlayers(false);
        });
    },
    [dispatch, info, t, toastApiError],
  );

  const body = useMemo(() => {
    if (!info || isLoading || teamFetching) {
      return <PlayerLoad />;
    }

    if (!player) {
      return (
        <div className="w-full px-6 pt-6">
          <div className="flex-center mt-24 flex flex-col">
            <div className="text body-text bold mb-2">
              <Trans>Такой игрок не найден</Trans>
            </div>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'teams':
        return (
          // `flex-1 min-h-0` — чтобы список растянулся на остаток страницы: только
          // на растянутой области пустое состояние встаёт по центру, а не под табы.
          <div className="mt-6 flex min-h-0 flex-1 flex-col">
            <TeamsList player={player} teams={teams ?? []} />
          </div>
        );
      case 'common':
        return <PlayerForm initialValues={player} onSubmit={handleSubmit} isLoading={isLoadingPlayers || teamFetching} />;
      case 'moments':
        return <PlayerMomentsPage player={player} />;
      default:
        return null;
    }
  }, [activeTab, handleSubmit, info, isLoading, isLoadingPlayers, player, teamFetching, teams]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'flex h-full flex-col px-6 pt-6'}>
        <div className={'mb-6'}>{player ? <PlayerEditFormTabs state={{ fromTeam: teamId }} /> : <></>}</div>
        {body}
      </div>
    </>
  );
}

export default PlayerViewPage;
